import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";

const euro = (n) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(n) || 0);

const vuoto = () => ({
  data: new Date().toLocaleDateString("en-CA"),
  descrizione: "",
  importo: "",
});

const etichette = {
  da_approvare: "Da approvare",
  approvato: "Approvata",
  rifiutato: "Rifiutata",
  annullato: "Annullata",
};

export default function LavorazioniExtraTab({ cantiereId }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(vuoto);
  const [modificaId, setModificaId] = useState(null);

  const base = `/cantieri/${encodeURIComponent(cantiereId)}/extra`;

  const { data: extra = [], isLoading, isError } = useQuery({
    queryKey: ["cantiere-extra", cantiereId],
    queryFn: () => api.get(base).then((r) => r.data),
    enabled: Boolean(cantiereId),
  });

  const aggiorna = () => {
    qc.invalidateQueries({ queryKey: ["cantiere-extra", cantiereId] });
    qc.invalidateQueries({ queryKey: ["cantiere-econ", cantiereId] });
    qc.invalidateQueries({ queryKey: ["cantiere", cantiereId] });
    qc.invalidateQueries({ queryKey: ["cantieri"] });
  };

  const operazione = useMutation({
    mutationFn: async ({ tipo, id, dati }) => {
      const url = id ? `${base}/${encodeURIComponent(id)}` : base;

      if (tipo === "crea") return api.post(url, dati);
      if (tipo === "modifica") return api.patch(url, dati);
      if (tipo === "stato") return api.patch(`${url}/stato`, dati);
      if (tipo === "elimina") return api.delete(url);
      if (tipo === "annulla") return api.patch(`${url}/annulla`, dati);

      throw new Error("Operazione non riconosciuta");
    },
    onSuccess: () => {
      toast.success("Lavorazione extra aggiornata");
      setForm(vuoto());
      setModificaId(null);
      aggiorna();
    },
    onError: (errore) => {
      const dettaglio = errore.response?.data?.detail;
      toast.error(typeof dettaglio === "string"
        ? dettaglio : "Operazione non riuscita");
    },
  });

  const salva = (evento) => {
    evento.preventDefault();

    const testoImporto = String(form.importo).trim().replace(",", ".");
    const importo = Number(testoImporto);

    if (
      !form.data ||
      form.descrizione.trim().length < 3 ||
      !/^\d+(\.\d{1,2})?$/.test(testoImporto) ||
      !Number.isFinite(importo) ||
      importo <= 0
    ) {
      toast.error("Controlla data, descrizione e importo");
      return;
    }

    operazione.mutate({
      tipo: modificaId ? "modifica" : "crea",
      id: modificaId,
      dati: {
        data: form.data,
        descrizione: form.descrizione.trim(),
        importo,
      },
    });
  };

  const cambiaStato = (voce, stato) => {
    const azione = stato === "approvato" ? "Approvare" : "Rifiutare";
    if (!window.confirm(`${azione} questa lavorazione?`)) return;

    operazione.mutate({
      tipo: "stato",
      id: voce.extra_id,
      dati: { stato },
    });
  };

  const elimina = (voce) => {
    if (!window.confirm("Eliminare questa lavorazione in attesa?")) return;
    operazione.mutate({ tipo: "elimina", id: voce.extra_id });
  };

  const annullaExtra = (voce) => {
    const motivazione = window.prompt("Motivazione annullamento (minimo 10 caratteri):");
    if (motivazione === null) return;
    if (motivazione.trim().length < 10) {
      toast.error("Inserisci una motivazione di almeno 10 caratteri");
      return;
    }
    operazione.mutate({
      tipo: "annulla",
      id: voce.extra_id,
      dati: { motivazione: motivazione.trim() },
    });
  };

  const iniziaModifica = (voce) => {
    setModificaId(voce.extra_id);
    setForm({
      data: voce.data,
      descrizione: voce.descrizione,
      importo: String(voce.importo),
    });
  };

  const totale = extra
    .filter((voce) => voce.stato === "approvato")
    .reduce((somma, voce) => somma + Number(voce.importo || 0), 0);

  return (
    <section className="bg-white border border-slate-200 p-5 space-y-5">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className="text-lg font-bold">Lavorazioni extra</h3>
          <p className="text-sm text-slate-500">
            Solo le lavorazioni approvate contribuiscono ai ricavi previsti.
          </p>
        </div>
        <div className="font-semibold">
          Totale approvato: {euro(totale)}
        </div>
      </div>

      <form onSubmit={salva} className="grid md:grid-cols-4 gap-3">
        <input
          type="date"
          required
          value={form.data}
          onChange={(e) => setForm({ ...form, data: e.target.value })}
          className="border border-slate-300 p-2"
          aria-label="Data lavorazione"
        />
        <input
          type="text"
          required
          minLength={3}
          maxLength={2000}
          placeholder="Descrizione lavorazione"
          value={form.descrizione}
          onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
          className="border border-slate-300 p-2 md:col-span-2"
        />
        <input
          type="text"
          inputMode="decimal"
          required
          placeholder="Importo €"
          value={form.importo}
          onChange={(e) => setForm({ ...form, importo: e.target.value })}
          className="border border-slate-300 p-2"
          aria-label="Importo lavorazione"
        />
        <div className="md:col-span-4 flex gap-2">
          <button type="submit" disabled={operazione.isPending}
            className="bg-orange-600 text-white px-4 py-2 disabled:opacity-50">
            {modificaId ? "Salva modifica" : "Aggiungi lavorazione"}
          </button>
          {modificaId && (
            <button type="button"
              onClick={() => { setModificaId(null); setForm(vuoto()); }}
              className="border border-slate-300 px-4 py-2">
              Annulla modifica
            </button>
          )}
        </div>
      </form>

      {isLoading && <p>Caricamento lavorazioni...</p>}
      {isError && (
        <p className="text-red-600">Errore nel caricamento delle lavorazioni.</p>
      )}
      {!isLoading && !isError && extra.length === 0 && (
        <p className="text-sm text-slate-500">
          Nessuna lavorazione extra registrata.
        </p>
      )}
      {!isLoading && !isError && extra.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="p-2">Data</th>
                <th className="p-2">Descrizione</th>
                <th className="p-2 text-right">Importo</th>
                <th className="p-2">Stato</th>
                <th className="p-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {extra.map((voce) => (
                <tr key={voce.extra_id} className="border-b">
                  <td className="p-2 whitespace-nowrap">{voce.data}</td>
                  <td className="p-2">{voce.descrizione}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {euro(voce.importo)}
                  </td>
                  <td className="p-2">
                    {etichette[voce.stato] || voce.stato}
                  </td>
                  <td className="p-2">
                    {voce.stato === "da_approvare" && (
                      <div className="flex flex-wrap gap-2">
                        <button type="button" disabled={operazione.isPending}
                          onClick={() => iniziaModifica(voce)}
                          className="underline disabled:opacity-50">
                          Modifica
                        </button>
                        <button type="button" disabled={operazione.isPending}
                          onClick={() => cambiaStato(voce, "approvato")}
                          className="text-green-700 underline disabled:opacity-50">
                          Approva
                        </button>
                        <button type="button" disabled={operazione.isPending}
                          onClick={() => cambiaStato(voce, "rifiutato")}
                          className="text-amber-700 underline disabled:opacity-50">
                          Rifiuta
                        </button>
                        <button type="button" disabled={operazione.isPending}
                          onClick={() => elimina(voce)}
                          className="text-red-700 underline disabled:opacity-50">
                          Elimina
                        </button>
                      </div>
                    )}
                    {voce.stato === "approvato" && (
                      <button type="button"
                        disabled={operazione.isPending}
                        onClick={() => annullaExtra(voce)}
                        className="text-red-700 underline disabled:opacity-50">
                        Annulla
                      </button>
                    )}
                    {voce.stato !== "da_approvare" && (
                      <span className="text-slate-400">Solo lettura</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
